import { useState } from 'react'
import { Loader2, Lock, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePermission } from '@/lib/permissions'
import {
  useCreateTransferReason,
  useDeleteTransferReason,
  useTransferReasons,
  useUpdateTransferReason,
} from '@/hooks/useStorageTransfers'
import type { StorageTransferDisposition } from '@/types/storage'

/**
 * Why a storage unit's contents get transferred, as a site-editable list.
 *
 * A catalog rather than a fixed enum because a site will have reasons nobody
 * here thought of — a validation protocol, a landlord's electrical shutdown.
 * The seven standard rows are seeded per tenant: their LABEL can be changed to
 * the site's own vocabulary, but their code cannot, and they cannot be deleted
 * — code branches on the disposition behind them, and existing transfers point
 * at the code.
 */

const DISPOSITIONS: Array<{ value: StorageTransferDisposition; label: string }> = [
  { value: 'RETURN_TO_SERVICE', label: 'The unit comes back' },
  { value: 'DECOMMISSION', label: 'The unit is retired' },
  { value: 'REMAINS_IN_SERVICE', label: 'The unit stays in service' },
]

export function TransferReasonCatalog() {
  const { can } = usePermission()
  const reasons = useTransferReasons()
  const createReason = useCreateTransferReason()
  const updateReason = useUpdateTransferReason()
  const deleteReason = useDeleteTransferReason()

  const mayEdit = can('inventory.configure')
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState('')
  const [disposition, setDisposition] = useState<StorageTransferDisposition>('RETURN_TO_SERVICE')
  const [error, setError] = useState<string | null>(null)

  /** The stable key, derived from the label so nobody has to invent one. */
  const codeFor = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60)

  const add = () => {
    const code = codeFor(label)
    if (code.length < 2) {
      setError('Give the reason a name of at least two characters.')
      return
    }
    setError(null)
    createReason.mutate(
      { code, label: label.trim(), disposition },
      {
        onSuccess: () => {
          setAdding(false)
          setLabel('')
          setDisposition('RETURN_TO_SERVICE')
        },
        onError: (e) => setError(e.message),
      },
    )
  }

  if (reasons.isLoading) {
    return (
      <p className='py-6 text-center text-sm text-muted-foreground'>
        <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
        Loading reasons…
      </p>
    )
  }

  const rows = reasons.data ?? []

  return (
    <div className='space-y-3'>
      <div className='flex items-start justify-between gap-3'>
        <div>
          <h3 className='text-sm font-medium'>Storage transfer reasons</h3>
          <p className='text-[13px] text-muted-foreground'>
            Offered when planning a replacement. Each carries the default outcome for the source
            unit, which the approver can still override.
          </p>
        </div>
        {mayEdit && !adding && (
          <Button size='sm' variant='outline' onClick={() => setAdding(true)}>
            <Plus className='mr-1 h-4 w-4' />
            Add reason
          </Button>
        )}
      </div>

      {adding && (
        <div className='flex flex-wrap items-end gap-2 rounded-md border p-3'>
          <div className='min-w-48 flex-1 space-y-1'>
            <Label>Name</Label>
            <Input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='Landlord electrical shutdown'
            />
          </div>
          <div className='min-w-56 space-y-1'>
            <Label>Usually means</Label>
            <Select
              value={disposition}
              onValueChange={(v) => setDisposition(v as StorageTransferDisposition)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISPOSITIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size='sm' disabled={createReason.isPending} onClick={add}>
            Add
          </Button>
          <Button
            size='sm'
            variant='ghost'
            onClick={() => {
              setAdding(false)
              setError(null)
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      {error && (
        <p className='rounded-md border border-red-200 bg-red-50 p-2 text-[13px] text-red-800'>
          {error}
        </p>
      )}

      <div className='overflow-x-persistent rounded-md border'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground'>
              <th className='px-3 py-2 font-medium'>Reason</th>
              <th className='px-3 py-2 font-medium'>Usually means</th>
              {mayEdit && <th className='px-3 py-2 font-medium' />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className='border-b last:border-0'>
                <td className='px-3 py-2'>
                  {mayEdit ? (
                    <Input
                      defaultValue={r.label}
                      className='h-8'
                      onBlur={(e) => {
                        const next = e.target.value.trim()
                        if (next && next !== r.label) {
                          updateReason.mutate(
                            { id: r.id, label: next },
                            { onError: (err) => setError(err.message) },
                          )
                        }
                      }}
                    />
                  ) : (
                    r.label
                  )}
                  {r.isSystem && (
                    <span className='ml-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground'>
                      <Lock className='h-3 w-3' />
                      standard
                    </span>
                  )}
                </td>
                <td className='px-3 py-2'>
                  {DISPOSITIONS.find((d) => d.value === r.disposition)?.label ?? r.disposition}
                </td>
                {mayEdit && (
                  <td className='px-3 py-2 text-right'>
                    {/* A standard row is renamable but undeletable — the seed
                        re-asserts it and transfers point at its code. */}
                    {!r.isSystem && (
                      <Button
                        size='sm'
                        variant='ghost'
                        disabled={deleteReason.isPending}
                        onClick={() =>
                          deleteReason.mutate(r.id, {
                            onError: (err) => setError(err.message),
                          })
                        }
                      >
                        <Trash2 className='h-4 w-4 text-muted-foreground' />
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
